/**
 * AIF-01 — AI Models LIST page (`/ai-models`, §11.1).
 *
 * Columns: Name, Type, Active (dot), Versions (count), Classes (count), row
 * action Open + an overflow menu (Edit / Deactivate-Activate / Delete) for
 * writers. Client-side Search (over name), Type select, and an "active only"
 * checkbox (default ON) narrow the already-fetched list (the list endpoint
 * returns every non-deleted model in one shot). Soft-deleted models never
 * appear (BE filters). Built on the canonical Data-page chrome, mirroring
 * `CatalogueListPage`.
 *
 * Read-only personas (no tenant_admin/expert_analyst/platform_admin) see no
 * ＋ button and no overflow menu (AC4).
 */
import { useMemo, useState, type ReactElement } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import type { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import {
  DataPageFooter,
  DataPageShell,
  DataPageToolbar,
  DataTable,
  SearchField,
  SingleSelectFilter,
  type Density,
} from "@aeriosiq/ui";
import type { AiFindingModel, AiFindingModelType } from "@aeriosiq/types";
import {
  useAiModelsQuery,
  useDeleteAiModelMutation,
  useUpdateAiModelMutation,
} from "./aiModelQueries.js";
import { useAiModelWritePermissions } from "./useAiModelWritePermissions.js";
import { modelTypeLabel } from "./modelType.js";
import { StatusDot } from "./StatusDot.js";
import { NewModelDialog } from "./NewModelDialog.js";
import { EditModelDialog } from "./EditModelDialog.js";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog.js";
import { EllipsisVerticalIcon, PlusIcon } from "@aeriosiq/ui/icons";

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

type TypeFilter = "all" | AiFindingModelType;

type ModelRow = AiFindingModel & { id: string };

export function AiModelsListPage(): ReactElement {
  const navigate = useNavigate();
  const modelsQuery = useAiModelsQuery();
  const { canWrite } = useAiModelWritePermissions();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [activeOnly, setActiveOnly] = useState(true);
  const [density] = useState<Density>("compact");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const [newOpen, setNewOpen] = useState(false);
  const [editModel, setEditModel] = useState<AiFindingModel | null>(null);
  const [deleteModel, setDeleteModel] = useState<AiFindingModel | null>(null);

  // Row overflow menu.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuModel, setMenuModel] = useState<AiFindingModel | null>(null);

  const updateMenuModel = useUpdateAiModelMutation(menuModel?.modelId ?? "");
  const deleteMutation = useDeleteAiModelMutation();

  const items: AiFindingModel[] = useMemo(
    () => modelsQuery.data?.items ?? [],
    [modelsQuery.data],
  );

  const filtered = useMemo<ModelRow[]>(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((m) => {
        if (activeOnly && !m.isActive) return false;
        if (typeFilter !== "all" && m.modelType !== typeFilter) return false;
        if (q.length === 0) return true;
        return m.modelName.toLowerCase().includes(q);
      })
      .map((m) => ({ ...m, id: m.modelId }));
  }, [items, search, typeFilter, activeOnly]);

  const total = filtered.length;

  function closeMenu(): void {
    setMenuAnchor(null);
    setMenuModel(null);
  }

  const columns = useMemo<GridColDef<ModelRow>[]>(
    () => [
      {
        field: "modelName",
        headerName: "Name",
        sortable: true,
        flex: 1,
        minWidth: 220,
        renderCell: (p) => (
          <Typography variant="body2" component="span">
            {p.row.modelName}
          </Typography>
        ),
      },
      {
        field: "modelType",
        headerName: "Type",
        sortable: true,
        width: 180,
        valueGetter: (_v, row) => modelTypeLabel(row.modelType),
      },
      {
        field: "isActive",
        headerName: "Active",
        sortable: true,
        width: 110,
        align: "center",
        headerAlign: "center",
        valueGetter: (_v, row) => (row.isActive ? 1 : 0),
        renderCell: (p) => <StatusDot active={p.row.isActive} />,
      },
      {
        field: "versionCount",
        headerName: "Versions",
        sortable: true,
        width: 120,
        type: "number",
        align: "right",
        headerAlign: "right",
      },
      {
        field: "classCount",
        headerName: "Classes",
        sortable: true,
        width: 120,
        type: "number",
        align: "right",
        headerAlign: "right",
      },
      {
        field: "actions",
        headerName: "Actions",
        // Visually hide the header text but keep it for screen readers (axe
        // empty-table-header rule).
        renderHeader: () => (
          <Box
            component="span"
            sx={{
              position: "absolute",
              width: 1,
              height: 1,
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
              whiteSpace: "nowrap",
            }}
          >
            Actions
          </Box>
        ),
        sortable: false,
        filterable: false,
        width: 140,
        align: "right",
        headerAlign: "right",
        renderCell: (p) => (
          <Box
            sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Button
              size="small"
              variant="text"
              aria-label={`Open ${p.row.modelName}`}
              onClick={() => {
                void navigate(
                  `/ai-models/${encodeURIComponent(p.row.modelId)}`,
                );
              }}
            >
              Open
            </Button>
            {canWrite ? (
              <IconButton
                size="small"
                aria-label={`More actions for ${p.row.modelName}`}
                onClick={(e) => {
                  setMenuAnchor(e.currentTarget);
                  setMenuModel(p.row);
                }}
              >
                <EllipsisVerticalIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Box>
        ),
      },
    ],
    [canWrite, navigate],
  );

  return (
    <DataPageShell title="AI Models">
      <DataPageToolbar
        ariaLabel="AI models toolbar"
        filtersTestId="ai-models-toolbar-filters"
        settingsTestId="ai-models-toolbar-settings"
        filters={
          <>
            <SearchField
              value={search}
              onChange={(next) => {
                setSearch(next);
                setPage(1);
              }}
              placeholder="Search"
              ariaLabel="Search models"
            />
            <SingleSelectFilter
              label="Type"
              width={180}
              value={typeFilter}
              options={[
                { value: "all", label: "All", display: "" },
                { value: "object_detection", label: "Object detection" },
                { value: "segmentation", label: "Segmentation" },
                { value: "classification", label: "Classification" },
              ]}
              onChange={(v) => {
                setTypeFilter(v as TypeFilter);
                setPage(1);
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={activeOnly}
                  onChange={(e) => {
                    setActiveOnly(e.target.checked);
                    setPage(1);
                  }}
                  size="small"
                />
              }
              label="Active only"
            />
          </>
        }
        settings={
          <>
            <Box sx={{ flex: 1 }} />
            {canWrite ? (
              <Button
                variant="contained"
                aria-label="New model"
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                onClick={() => {
                  setNewOpen(true);
                }}
              >
                New model
              </Button>
            ) : null}
          </>
        }
      />

      {modelsQuery.isLoading ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: 6,
          }}
        >
          <CircularProgress aria-label="Loading models" />
        </Box>
      ) : modelsQuery.isError ? (
        <Typography color="error" sx={{ p: 2 }}>
          Couldn&apos;t load AI models.
        </Typography>
      ) : items.length === 0 ? (
        <Box sx={{ flex: 1, py: 4 }}>
          <Typography color="text.secondary">
            No AI models yet. Create one to get started.
          </Typography>
        </Box>
      ) : (
        <DataTable<ModelRow>
          rows={filtered}
          columns={columns}
          ariaLabel="AI models"
          density={density}
          getRowId={(row) => row.id}
          paginationMode="client"
          sortingMode="client"
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(m) => {
            const nextPage = m.page + 1;
            if (m.pageSize !== pageSize) {
              setPageSize(m.pageSize);
              setPage(1);
            } else if (nextPage !== page) {
              setPage(nextPage);
            }
          }}
          onRowClick={(p: GridRowParams<ModelRow>) => {
            void navigate(`/ai-models/${encodeURIComponent(p.row.modelId)}`);
          }}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      )}

      <DataPageFooter
        total={total}
        entityLabel="models"
        entityLabelSingular="model"
        page={page}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        totalTestId="ai-models-footer-total"
      />

      <Menu
        anchorEl={menuAnchor}
        open={menuAnchor !== null}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => {
            setEditModel(menuModel);
            closeMenu();
          }}
        >
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={updateMenuModel.isPending}
          onClick={() => {
            const m = menuModel;
            if (!m) return;
            updateMenuModel.mutate({ isActive: !m.isActive });
            closeMenu();
          }}
        >
          <ListItemText>
            {menuModel?.isActive ? "Deactivate" : "Activate"}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteModel(menuModel);
            closeMenu();
          }}
        >
          <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <NewModelDialog
        open={newOpen}
        onClose={() => {
          setNewOpen(false);
        }}
        onCreated={(created) => {
          setNewOpen(false);
          void navigate(`/ai-models/${encodeURIComponent(created.modelId)}`);
        }}
      />

      {editModel ? (
        <EditModelDialog
          open
          model={editModel}
          onClose={() => {
            setEditModel(null);
          }}
          onSaved={() => {
            setEditModel(null);
          }}
        />
      ) : null}

      {deleteModel ? (
        <ConfirmDeleteDialog
          open
          title="Delete AI model"
          message={`Delete model "${deleteModel.modelName}"? It will be removed from the list. This is a soft delete.`}
          pending={deleteMutation.isPending}
          onClose={() => {
            setDeleteModel(null);
          }}
          onConfirm={() => {
            deleteMutation.mutate(deleteModel.modelId, {
              onSuccess: () => {
                setDeleteModel(null);
              },
            });
          }}
        />
      ) : null}
    </DataPageShell>
  );
}
